import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import RewardIcon from 'components/RewardIcon';
import {
  isAvailable,
  isExpired,
  isRedeemed,
  isStandardPrizeType
} from 'utils/rewards';
import {
  onlineOfferPropTypes,
  partnerRewardPropTypes,
  patronRewardPropTypes,
  plsRewardPropTypes
} from 'utils/shapes';
import InfoIcon from 'resources/UI_ButtonInfo.png';
import TreeBackground from 'resources/large_tree_pls.png';
import WIcon from 'resources/W_icon_reward_banner.png';
import TreeGraphic from '../GetMoreRewardsCard/assets/Miami graphic.png';
import RewardCardModal from './RewardCardModal';
import ExpirationTimer from './RewardExpirationTimer';
import ArrowUpIcon from './assets/arrow-up-right-from-square-solid.png';
import './ProfileRewardCard.scss';

const ProfileRewardCard = ({
  reward,
  onlineOfferFired,
  onRedeemOnlineOffer,
  canceled = false
}) => {
  const [showRewardCardModal, setShowRewardCardModal] = useState(false);

  const {
    isUberReward,
    isPlsReward,
    name,
    description,
    url,
    type,
    rewardId,
    location,
    redeemLink,
    quantity,
    onlineOfferUuid
  } = reward;

  const rewardIsAvailable = isAvailable(reward) && !canceled;
  const rewardIsExpired = isExpired(reward);
  const rewardIsRedeemed = isRedeemed(reward);

  const startDateDisplay = reward.startDateDisplay.split(' ')[0];
  const endDateDisplay = reward.endDateDisplay.split(' ')[0];
  const unavailableSuffix = rewardIsAvailable ? '' : 'unavailable-reward';
  const prizeCodeSuffix = rewardId.length < 16 ? '' : 'long-prize-code';
  const titleBannerIcon = isPlsReward ? TreeGraphic : WIcon;
  const titleBannerClassKeyword = isPlsReward ? 'tree' : 'w';

  const now = moment();
  const isWithinValidDateRange =
    moment(reward.startDate).isSameOrBefore(now) &&
    now.isBefore(moment(reward.endDate));
  const shouldRenderRedeemButton =
    reward.isSoftOffer &&
    isWithinValidDateRange &&
    reward.shouldShowRedeemButton &&
    onRedeemOnlineOffer;
  const shouldRenderDownloadNotice =
    rewardIsAvailable && !isStandardPrizeType(reward);

  const isUberVoucherRedeemable =
    reward.isUberReward &&
    rewardIsAvailable &&
    !reward.claimed &&
    !rewardIsExpired;

  const magicCityText = isPlsReward && (
    <>
      <span className="magic-city-text-1">Magic</span>
      <span className="magic-city-text-2">City</span>
    </>
  );

  const infoButton = isPlsReward && (
    <button
      className="reward-card-info-button"
      onClick={() => setShowRewardCardModal(true)}
      type="button"
    >
      <img src={InfoIcon} alt="Open reward details" />
    </button>
  );

  const renderRewardName = () => {
    return (
      <div
        className="reward-name"
        ref={ref => {
          if (ref?.scrollWidth > ref?.clientWidth) {
            const tooltip = ref.querySelector('.tooltip-text');
            tooltip.classList.add('truncated');
          }
        }}
      >
        {name}
        <span class="tooltip-text">{name}</span>
      </div>
    );
  };

  const prizeCode = () => {
    return (
      <div className="reward-prize-code-wrapper">
        <div className="reward-prize-code-title">
          Prize Code:
          <div className={`reward-prize-code ${prizeCodeSuffix}`}>
            {rewardId}
          </div>
        </div>
      </div>
    );
  };

  const renderExpirationTimer = () => {
    if (reward.isUberReward) {
      if (!rewardIsAvailable) {
        return null;
      } else {
        return <ExpirationTimer endDate={reward.endDate} />;
      }
    }

    if (rewardIsRedeemed) {
      return 'Redeemed';
    } else if (canceled) {
      return 'Canceled';
    } else if (!rewardIsAvailable) {
      return 'Expired';
    } else {
      return <ExpirationTimer endDate={reward.endDate} />;
    }
  };

  const renderUberEndDate = () => {
    let validityText = reward.eventDate;
    if (rewardIsRedeemed) {
      return `Redeemed ${validityText}`;
    } else if (canceled) {
      return `Canceled ${validityText}`;
    } else if (!rewardIsAvailable) {
      return `Expired ${validityText}`;
    } else if (reward.claimed) {
      return `Claimed ${validityText}`;
    }
    return validityText;
  };

  const uberRewardContent = () => {
    return (
      <div className="reward-prize-code-wrapper uber-reward">
        <div className="reward-prize-code-title">
          <div>{location}</div>
          {`Voucher Code: ${rewardId}`}
        </div>
        <div className="reward-prize-description">{description}</div>
        <div className="reward-prize-validity">
          Valid {reward.startDateDisplay} - {reward.endDateDisplay}
        </div>
        {isUberVoucherRedeemable && (
          <>
            <a
              className="reward-uber-voucher-redeem"
              href={redeemLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <p>
                CLAIM{' '}
                <span>
                  <img src={ArrowUpIcon} alt="" />
                </span>
              </p>
            </a>
          </>
        )}
      </div>
    );
  };

  const uberRewardValidDates = () => {
    return (
      <div className="reward-valid-dates uber-reward">
        {renderUberEndDate()}
      </div>
    );
  };

  const plsRewardModalContent = () => {
    return (
      <>
        <RewardCardModal
          show={showRewardCardModal}
          reward={reward}
          closeModal={() => setShowRewardCardModal(false)}
          isRewardAvailable={rewardIsAvailable}
        />
        <img
          className="reward-card-tree-background"
          src={TreeBackground}
          alt="Tree graphic for reward card background"
        />
      </>
    );
  };

  const rewardContent = () => {
    if (isUberReward) {
      return null;
    }

    const validDatesClassName = `reward-valid-dates ${
      shouldRenderRedeemButton || shouldRenderDownloadNotice
        ? 'with-redeem-option'
        : ''
    }`;
    const onlineOfferRedeemButtonClass = `btn-success redeem-soft-offer ${
      onlineOfferFired ? 'disabled' : ''
    }`;
    const handleRedeem = () => {
      if (!onlineOfferFired) {
        onRedeemOnlineOffer(onlineOfferUuid, quantity);
      }
    };

    return (
      <>
        {!isPlsReward && (
          <div className="reward-quantity">Quantity: {quantity}</div>
        )}
        {shouldRenderDownloadNotice && (
          <div className="download-to-redeem">
            Download the Casinoverse App to claim this reward!
          </div>
        )}

        {!shouldRenderDownloadNotice && shouldRenderRedeemButton && (
          <Button
            className={onlineOfferRedeemButtonClass}
            onClick={handleRedeem}
          >
            Redeem
          </Button>
        )}
        <div className={validDatesClassName}>
          {startDateDisplay} - {endDateDisplay}
        </div>
      </>
    );
  };

  const cardClassName = `profile-reward-card ${unavailableSuffix} ${
    isUberReward ? 'uber-reward' : ''
  }`;

  return (
    <div className={cardClassName}>
      {magicCityText}
      <img
        className={`reward-card-${titleBannerClassKeyword}-graphic`}
        src={titleBannerIcon}
        alt=""
      />
      {renderRewardName()}
      <div className="reward-expiration-timer">{renderExpirationTimer()}</div>
      <div className="reward-icon-image-container">
        <RewardIcon
          className="reward-icon-image"
          url={url}
          type={type}
          alt="reward icon"
          size={100}
        />
        {infoButton}
      </div>
      {!isUberReward && prizeCode()}
      {isUberReward ? uberRewardContent() : rewardContent()}
      {isUberReward && !isUberVoucherRedeemable && uberRewardValidDates()}
      {isPlsReward && plsRewardModalContent()}
    </div>
  );
};

ProfileRewardCard.propTypes = {
  reward: PropTypes.oneOfType([
    PropTypes.shape(patronRewardPropTypes),
    PropTypes.shape(onlineOfferPropTypes),
    PropTypes.shape(plsRewardPropTypes),
    PropTypes.shape(partnerRewardPropTypes)
  ]).isRequired,
  onlineOfferFired: PropTypes.bool,
  onRedeemOnlineOffer: PropTypes.func,
  available: PropTypes.bool,
  redeemed: PropTypes.bool,
  canceled: PropTypes.bool
};

export default ProfileRewardCard;
